import { getUserState } from '@carvana/core-auth';
import { getAppState, setAppState } from './State';
import { addCardPosition } from '../utils/helpers';
import {
  getGarageCardsDataApi,
  postCancelPreOrder,
  postCancelPrePurchase,
} from '../services/GaragePageService';
import { OneCarStatus, PurchaseState, ReferenceTypes } from '../constants';
import { cancellationPayloadTypes } from '../types';

/* 
    Garage Page Store -- is a wrapper around state and data access, 
    abstracts away data access logic out of the components. This 
    store is for garage page data access calls.

 */

const environment = process.env.NODE_ENV;

export const filterVehiclesByPurchaseValues = (
  purchases: any,
  purchaseValues: string[]
) => {
  return purchases.filter((vehicleInfo: any) =>
    purchaseValues.some((status) => status === vehicleInfo.purchaseState)
  );
};

export const filterPrePurchaseVehicles = (purchases: any) => {
  const { IN_PROGRESS, ORDER_PLACED } = PurchaseState;
  return purchases.filter((vehicleInfo: any) =>
    [IN_PROGRESS, ORDER_PLACED].some(
      (status) => status === vehicleInfo.purchaseState
    )
  );
};

export const filterPostPurchaseVehicles = (purchases: any) => {
  const { CANCELLED, SALE_COMPLETE, POST_SALE } = PurchaseState;
  return purchases.filter((vehicleInfo: any) =>
    [CANCELLED, SALE_COMPLETE, POST_SALE].some(
      (status) => status === vehicleInfo.purchaseState
    )
  );
};

export const getDefaultVisibleVehicleCount = (
  prePurchases: any,
  preOrders: any,
  postPurchases: any,
  appraisals?: any
) => {
  const vehicleCount =
    prePurchases?.length +
    postPurchases?.length +
    preOrders?.length +
    appraisals?.length;

  return vehicleCount;
};

export const getGarageData = async (): Promise<any> => {
  const state = getAppState();

  // return state if populated with data
  if (
    state?.prePurchaseVehicles?.length > 0 ||
    state?.preOrderVehicles?.length > 0 ||
    state?.postPurchaseVehicles?.length > 0 ||
    state?.appraisals?.length > 0
  ) {
    return state;
  }

  // update state with and then return data
  const response = await getGarageCardsDataApi(environment);
  if (response.data?.data) {
    response.data = response.data.data;
  }
  if (response.data?.purchases) {
    state.isGarageDataFetched = true;
    state.prePurchaseVehicles = filterPrePurchaseVehicles(
      response.data.purchases
    );
    state.postPurchaseVehicles = filterPostPurchaseVehicles(
      response.data.purchases
    );
  }
  if (response.data?.appraisals) {
    state.isGarageDataFetched = true;
    state.appraisals = response.data.appraisals;
  }

  if (response.data?.preOrders) {
    state.isGarageDataFetched = true;
    state.preOrderVehicles = response.data.preOrders;
  }
  state.vehicleCount = getDefaultVisibleVehicleCount(
    state.prePurchaseVehicles,
    state.preOrderVehicles,
    state.postPurchaseVehicles,
    state.appraisals
  );
  setGarageActiveState(state);
  setOneCarOnlyStatus(state);

  if (response.data?.error || response.error) {
    state.garageDataError = true;
  } else {
    // Add Card Position Property based on render order
    const result = addCardPosition(state);
    state.prePurchaseVehicles = result.prePurchaseVehicles;
    state.preOrderVehicles = result.preOrderVehicles;
    state.postPurchaseVehicles = result.postPurchaseVehicles;
    state.appraisals = result.appraisals;
  }

  setAppState(state);
  return state;
};

export const setGarageActiveState = (state: any): any => {
  state.garageActiveState = state.vehicleCount > 0;
};

export const setOneCarOnlyStatus = (state: any): any => {
  state.oneCarOnlyStatus = '';
  if (state.vehicleCount === 1) {
    if (state.prePurchaseVehicles?.length === 1) {
      state.oneCarOnlyStatus = OneCarStatus.PRE_PURCHASE;
      return state;
    }
    if (state.preOrderVehicles?.length === 1) {
      state.oneCarOnlyStatus = OneCarStatus.PRE_ORDER;
      return state;
    }
    if (state.postPurchaseVehicles?.length === 1) {
      state.oneCarOnlyStatus = OneCarStatus.POST_PURCHASE;
      state.ovpReferenceType = ReferenceTypes.VEHICLE;
      state.showBacklink = false;
      state.ovpVehicleReferenceId = state.postPurchaseVehicles[0].vehicle.id;
      return state;
    }
    if (state.appraisals?.length === 1) {
      state.oneCarOnlyStatus = OneCarStatus.APPRAISAL;
      state.ovpReferenceType = ReferenceTypes.APPRAISAL;
      state.showBacklink = false;
      state.ovpVehicleReferenceId = state.appraisals[0].appraisalId;
      return state;
    }
  }
};

export const getLandingPageState = async () => {
  const { isLoggedIn } = getUserState();

  if (isLoggedIn) {
    await getGarageData();
  }

  return getAppState();
};

export const deletePreOrder: any = async (
  cancellationPayload: cancellationPayloadTypes,
  env: string,
  appInsightCallback?: (params: any) => void
) => {
  const result = await postCancelPreOrder(cancellationPayload, env);
  return result;
};

export const deletePrePurchase: any = async (
  cancellationPayload: cancellationPayloadTypes,
  env: string,
  appInsightCallback?: (params: any) => void
) => {
  const result = await postCancelPrePurchase(cancellationPayload, env);
  return result;
};
